import type { VoidFunctionComponent } from 'react';

import { DEFAULT_VCS_APP, VCS_APP_DISPLAY } from '../../../../../shared/vcs/githubApp/apps';
import { useGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { useUninstallGitHubApp } from '../../../../hooks/api/vcs/githubApp/useUninstallGitHubApp';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import { icons, IconText } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import ServiceImportHeader from '../ServiceImportHeader';
import ServiceImportRepositories from '../ServiceImportRepositories';
import { ServiceMeta } from '../types';
import type { ServiceQueryParameters } from '../../../../types';

import styles from '../ServiceImport.module.less';

const SERVICE_DETAILS = ServiceMeta['github-app'];
const PAGE_SIZE = 30;
const DEFAULT_APP_DISPLAY = VCS_APP_DISPLAY[DEFAULT_VCS_APP];

// The shell has no browse/search/pagination yet, so the header renders framing only.
const EMPTY_QUERY: ServiceQueryParameters = {
  q: '',
  page: 1,
  pageSize: PAGE_SIZE,
  filterType: '',
  filterValue: '',
  instanceName: '',
};

const noop = () => {};
const asyncNoop = async () => {};

/** The service label/frame, reused from the legacy import layout. Controls are hidden until browse lands. */
const ImportHeader: VoidFunctionComponent<{ isInstalled: boolean; isLoading: boolean }> = ({
  isInstalled,
  isLoading,
}) => (
  <div className="panel">
    <div className="panel-body padded padded-small">
      <ServiceImportHeader
        serviceDetails={SERVICE_DETAILS}
        filters={[]}
        query={EMPTY_QUERY}
        instanceNames={[]}
        instanceIsConnected={isInstalled}
        shouldDisplayImportControls={false}
        selectedRepoLength={0}
        isLoading={isLoading}
        searchValue=""
        onSelectInstanceName={asyncNoop}
        onSelectFilter={asyncNoop}
        onSearchChange={noop}
        onImport={asyncNoop}
        onBulkImport={noop}
        onConnectToService={noop}
      />
    </div>
  </div>
);

/** Left column: the auth-note/docs card and, once installed, a disconnect action. */
const ImportSidebar: VoidFunctionComponent<{ isInstalled: boolean }> = ({ isInstalled }) => {
  const { onUninstall, isUninstalling } = useUninstallGitHubApp();

  return (
    <div className="col-xs-3">
      {(SERVICE_DETAILS.authNote || SERVICE_DETAILS.docsURL) && (
        <div className="card card-flat">
          <IconText icon={icons.questionCircle} text={SERVICE_DETAILS.authNote as string} />
          {SERVICE_DETAILS.docsURL && (
            <a
              className="btn btn-default btn-block margin-top"
              href={SERVICE_DETAILS.docsURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={styles.docsLink}>
                <IconText icon={icons.book} text={SERVICE_DETAILS.docsLinkText || 'Documentation'} />
              </span>
            </a>
          )}
        </div>
      )}
      {isInstalled && (
        <Button
          type={BUTTON_TYPE.ERROR}
          size={BUTTON_SIZE.SMALL}
          icon={icons.powerOff}
          isDisabled={isUninstalling}
          onClick={() => onUninstall(DEFAULT_VCS_APP)}
        >
          Disconnect from {SERVICE_DETAILS.displayName}
        </Button>
      )}
    </div>
  );
};

/** Right column: the shared repositories table. Not installed → install CTA; installed → repos (loader for now). */
const RepositoriesColumn: VoidFunctionComponent<{ isInstalled: boolean }> = ({ isInstalled }) => {
  const startInstall = useStartGitHubAppInstall();

  return (
    <div className="col-xs-9">
      <ServiceImportRepositories
        repositories={[]}
        selectedRepos={[]}
        page={1}
        pageSize={PAGE_SIZE}
        // vcs-service has a single "installed" state and no separate OAuth
        // "connect the service" step, so isConnected is always satisfied here.
        // hasInstallations is the real gate: false → install CTA, true → repos.
        isConnected
        hasInstallations={isInstalled}
        // TODO(next ticket): drive from useGitHubAppRepositories. Until then, the
        // installed state shows the shared loader instead of a "no repos" message.
        isLoading={isInstalled}
        onPageUp={asyncNoop}
        onPageDown={asyncNoop}
        onBranchChange={noop}
        onRowRedirect={noop}
        onSelectRepository={noop}
        onImportRepository={noop}
        onConnectToService={noop}
        onInstallGithubApp={() => startInstall.mutate(DEFAULT_VCS_APP)}
        githubAppDisplay={DEFAULT_APP_DISPLAY}
      />
    </div>
  );
};

/** Body below the header: resolves the loading / error / connected states. */
const ImportBody: VoidFunctionComponent<{ isLoading: boolean; isError: boolean; isInstalled: boolean }> = ({
  isLoading,
  isError,
  isInstalled,
}) => {
  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return <ErrorAlert status={{ error: 'Failed to load GitHub App status. Please try again.' }} />;
  }
  return (
    <div className="row">
      <ImportSidebar isInstalled={isInstalled} />
      <RepositoriesColumn isInstalled={isInstalled} />
    </div>
  );
};

/**
 * Entry container for the vcs-service GitHub App import flow: an org installs the default
 * app here (step one of import, not a settings action), then sees the connected state.
 * Reuses the legacy header + repositories components for layout parity; repository
 * browsing/importing lands in follow-up tickets.
 */
export const VcsGitHubAppImport: VoidFunctionComponent = () => {
  const { data: installation, isLoading, isError } = useGitHubAppInstallation(DEFAULT_VCS_APP);

  const isInstalled = Boolean(installation?.installed);

  return (
    <div className="fossa-service-import">
      <ImportHeader isInstalled={isInstalled} isLoading={isLoading} />
      <ImportBody isLoading={isLoading} isError={isError} isInstalled={isInstalled} />
    </div>
  );
};
